import type { ReactNode } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/cms/ui/tabs';
import LureoButton from '@/common/LureoButton';
import { ArrowLeftIcon, CreditCardIcon, GearIcon, ShieldIcon, UserIcon } from '@phosphor-icons/react';
import { PageTitle } from '@/components/ui/text';

interface AccountPageProps {
  /** Back to the dashboard, from the header's button. */
  onBack: () => void;
  /** Whether the user owns the org: only an owner sees the Subscription tab. */
  owner: boolean;
  /** What each tab shows. */
  profile: ReactNode;
  security: ReactNode;
  subscription: ReactNode;
  settings: ReactNode;
  /** Shown above the tabs, such as the account failing to load. */
  notice?: ReactNode;
}

/** The account page's header bar and its tabs. My Account and the design system's preview both draw it. */
export function AccountPage({ onBack, owner, profile, security, subscription, settings, notice }: AccountPageProps) {
  return (
    <>
      {/* Header */}
      <div className="sticky top-0 z-lureo-layer-bar surface-bar">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-4">
              <LureoButton variant="no-outline" onClick={onBack} className="h-9 w-9 p-0 md:w-auto md:px-4 rounded-lureo-radius-button-pill">
                <ArrowLeftIcon className="h-5 w-5 md:mr-2" />
                <span className="hidden md:inline pr-2">Dashboard</span>
              </LureoButton>
              <div className="h-6 w-px bg-lureo-line" />
              <PageTitle>My Account</PageTitle>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-6 md:py-8">
        <div className="mx-auto max-w-4xl">
          {notice}

          <Tabs defaultValue="profile" className="w-full">
            <TabsList className={`grid w-full ${owner ? 'grid-cols-4' : 'grid-cols-3'}`}>
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <UserIcon className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2">
                <ShieldIcon className="h-4 w-4" />
                Security
              </TabsTrigger>
              {owner && (
                <TabsTrigger value="subscription" className="flex items-center gap-2">
                  <CreditCardIcon className="h-4 w-4" />
                  Subscription
                </TabsTrigger>
              )}
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <GearIcon className="h-4 w-4" />
                Settings
              </TabsTrigger>
            </TabsList>

            <div className="mt-6 space-y-8">

            <TabsContent value="profile">{profile}</TabsContent>

            <TabsContent value="security">{security}</TabsContent>

            {owner && <TabsContent value="subscription">{subscription}</TabsContent>}

            <TabsContent value="settings">{settings}</TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
}
